<script setup lang="ts">
import type { ICommodity, ICrypto, IRealEstate, IStock } from '~/@types/assets'
import AssetsAdapter from '@adapters/AssetsAdapter'
import { formatCurrency } from '@helpers/numberHelper'
import ToggleSwitch from 'primevue/toggleswitch'
import { defineEmits, defineProps, ref } from 'vue'
import SubtitleComponent from '~/components/elements/SubtitleComponent.vue'
import { useAssetsWatchingStore } from '~/stores/assetsWatching'

const props = defineProps({
  isOpen: { type: Boolean },
  isLoading: { type: Boolean },
  asset: null
})

const emit = defineEmits(["update:isOpen", "closed"])

const assetWatchStore = useAssetsWatchingStore()
const showBottom = ref(false)
const isWatching = ref(false)
const title = ref<string>('')
const subtitle = ref<string>('')
const isLoading = ref(false)
const sentimentLoading = ref(false)
const sentimentScore = ref<number>(0)
const sentimentAnalysis = ref<string | null>(null)

async function setAssetInfo() {
  const _asset = props.asset
  if (!_asset)
    return

  if (_asset.type === 'real_estate') {
    title.value = 'Real Estate'
    subtitle.value = _asset.address ?? ''
  }
  else {
    title.value = _asset.symbol
    subtitle.value = _asset.name
    sentimentScore.value = _asset.sentiment_score
    sentimentAnalysis.value = _asset.sentiment_analysis
    isWatching.value = _asset.is_user_watching
  }

  if (!sentimentAnalysis.value) {
    sentimentLoading.value = true
    const analysis = await AssetsAdapter.runAiAnalysis(_asset.id)
    sentimentScore.value = analysis.score
    sentimentAnalysis.value = analysis.analysis
    sentimentLoading.value = false
  }
}

function isWatchingChanged(value: boolean): void {
  if (!props.asset)
    return

  if (value) {
    assetWatchStore.watchAsset(props.asset.id)
  }
  else {
    assetWatchStore.unwatchAsset(props.asset.id)
  }
}

function resetData() {
  title.value = ''
  subtitle.value = ''
  sentimentScore.value = 0
  sentimentAnalysis.value = null
}

watch(() => props.isOpen, (val) => {
  showBottom.value = val
  if (!val)
    resetData()
})

watch(() => props.asset, () => {
  setAssetInfo()
})

watch(showBottom, (val) => {
  emit('update:isOpen', val)
  if (!val) {
    emit('closed')
  }
})
</script>

<template>
  <van-action-sheet v-model:show="showBottom" :title="isLoading ? '...' : title">
    <Loader v-if="isLoading || !asset" />

    <section v-else class="px-12">
      <header class="flex justify-between items-center mb-10">
        <div>
          <TitleComponent :title="title" :no-margin="true" />
          <p class="text-sm text-gray-800">
            {{ subtitle }}
          </p>
          <BadgeComponent v-if="asset.type === 'stock'" class="mr-4" :title="asset.is_shariah_compliant ? 'Halal' : 'Non-Halal'" :kind="asset.is_shariah_compliant ? 'success' : 'fail'" />
        </div>
        <div class="flex justify-end">
          <SubtitleComponent title="Watching" class="mr-2" />
          <ToggleSwitch v-model="isWatching" @update:model-value="isWatchingChanged" />
        </div>
      </header>

      <section class="mb-12 flex justify-between ">
        <template v-if="asset.type !== 'real_estate'">
          <KeyVal title="Current price" :value="formatCurrency(asset.latest_value, asset.currency)" />
        </template>

        <KeyVal v-if="asset.type !== 'real_estate'" title="Sentiment">
          <SentimentScore :sentiment="sentimentScore" />
        </KeyVal>
      </section>

      <section v-if="asset.type !== 'real_estate'" class="">
        <SubtitleComponent title="AI Analysis" class="mb-4 " />
        <Loader v-if="sentimentLoading" />
        <template v-else>
          <p>{{ sentimentAnalysis }}</p>
        </template>
      </section>
    </section>
  </van-action-sheet>
</template>
